import React from "react";
import {
    Card,
    Input,
    DatePicker,
    Button,
    Modal,
    Row,
    Col,
    Typography,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { DroppableLane } from "./DroppableLane";
import { ConditionCard } from "./ConditionCard";
import { EarlyBirdRuleWithUiId } from "app/pages/EarlyBirdOfferBuilder/actionTypes";
import type { BuilderPageLoadedData } from "app/pages/EarlyBirdOfferBuilder/actionTypes";

interface RuleCardProps {
    ruleIndex: number;
    rule: EarlyBirdRuleWithUiId;
    pricingYear: number;
    lookups: BuilderPageLoadedData;
    onUpdateName: (value: string) => void;
    onUpdateEndDate: (value: string) => void;
    onRemoveRule: () => void;
    onRemoveCondition: (lane: "include" | "exclude", uiId: string) => void;
    onEditCondition: (
        lane: "include" | "exclude",
        condition: any
    ) => void;
    onLaneClick?: (ruleIndex: number, lane: "include" | "exclude") => void;
}

export const RuleCard: React.FC<RuleCardProps> = ({
      ruleIndex,
      rule,
      pricingYear,
      lookups,
      onUpdateName,
      onUpdateEndDate,
      onRemoveRule,
      onRemoveCondition,
      onEditCondition,
      onLaneClick,
  }) => {
    return (
        <Card
            size="small"
            style={{ marginBottom: 16 }}
            title={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                    }}
                >
                    <Input
                        placeholder="Rule Name *"
                        value={rule.name}
                        onChange={(e) => onUpdateName(e.target.value)}
                        style={{ width: 220 }}
                    />

                    <Typography.Text type="secondary">
                        applies <strong>{pricingYear - 1}</strong> prices to all
                        bookings received before
                    </Typography.Text>

                    <DatePicker
                        value={
                            rule.earlyBirdEndDate
                                ? dayjs(rule.earlyBirdEndDate)
                                : null
                        }
                        onChange={(date) =>
                            onUpdateEndDate(
                                date ? date.format("YYYY-MM-DD") : ""
                            )
                        }
                        format="DD-MMM-YYYY"
                        placeholder="Valid Until *"
                    />

                    <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                            Modal.confirm({
                                title: "Delete rule",
                                content: "Remove this rule?",
                                okText: "Delete",
                                okType: "danger",
                                cancelText: "Cancel",
                                onOk: onRemoveRule,
                            });
                        }}
                    />
                </div>
            }
        >
            <Row gutter={16}>
                <Col span={12}>
                    <div style={{ fontWeight: 500, marginBottom: 6 }}>
                        Qualifying Conditions
                    </div>
                    <div
                        style={{
                            marginBottom: 4,
                            fontSize: 12,
                            color: "#666",
                        }}
                    >
                        Define the booking criteria that qualify for the Early Bird Discount. All conditions must match.
                    </div>

                    <DroppableLane
                        ruleIndex={ruleIndex}
                        lane="include"
                        onClick={() => onLaneClick?.(ruleIndex, "include")}
                    >
                        {rule.include.map((c) => (
                            <ConditionCard
                                key={(c as any).uiId}
                                condition={c}
                                lookups={lookups}
                                onRemove={() =>
                                    onRemoveCondition("include", (c as any).uiId)
                                }
                                onEdit={() => onEditCondition("include", c)}
                            />
                        ))}
                    </DroppableLane>
                </Col>

                <Col span={12}>
                    <div style={{ fontWeight: 500, marginBottom: 6 }}>
                        Exclusions
                    </div>
                    <div
                        style={{
                            marginBottom: 4,
                            fontSize: 12,
                            color: "#666",
                        }}
                    >
                        Define booking criteria that prevent the Early Bird Discount from being applied.
                    </div>

                    <DroppableLane
                        ruleIndex={ruleIndex}
                        lane="exclude"
                        onClick={() => onLaneClick?.(ruleIndex, "exclude")}
                    >
                        {rule.exclude.map((c) => (
                            <ConditionCard
                                key={(c as any).uiId}
                                condition={c}
                                lookups={lookups}
                                onRemove={() =>
                                    onRemoveCondition("exclude", (c as any).uiId)
                                }
                                onEdit={() => onEditCondition("exclude", c)}
                            />
                        ))}
                    </DroppableLane>
                </Col>
            </Row>
        </Card>
    );
};